'use client';

import { useCallback, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { Activity, ExternalLink, ScrollText, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { Link } from '@/i18n/routing';
import { usePermissions } from '@/hooks/use-permissions';
import { useRelativeTime } from '@/hooks/use-relative-time';
import { useHashDialog } from '@/hooks/use-hash-dialog';
import { SYSTEM_LOGS_HASH } from '../constants';
import { systemLogQueries } from '../services/system-logs.queries';
import { ActionIcon, getActionStyle } from '../utils/action-style';
import { useLogFormatters } from '../utils/use-log-formatters';
import type { AuditLog } from '../types';

function LogItemSkeleton() {
  return (
    <div className='flex items-start gap-3 px-1 py-3'>
      <div className='bg-muted h-9 w-9 shrink-0 animate-pulse rounded-full' />
      <div className='flex-1 space-y-2'>
        <div className='flex items-center justify-between'>
          <div className='bg-muted h-3.5 w-24 animate-pulse rounded' />
          <div className='bg-muted h-3 w-14 animate-pulse rounded' />
        </div>
        <div className='flex items-center gap-2'>
          <div className='bg-muted h-5 w-16 animate-pulse rounded-md' />
          <div className='bg-muted h-3 w-20 animate-pulse rounded' />
        </div>
      </div>
    </div>
  );
}

function LogItem({
  log,
  relativeTime,
  systemLabel,
  translateAction,
  translateSubjectType
}: {
  log: AuditLog;
  relativeTime: (d: string) => string;
  systemLabel: string;
  translateAction: (action: string) => string;
  translateSubjectType: (subjectType: string) => string;
}) {
  const style = getActionStyle(log.action);
  const subjectLabel = log.subject_type
    ? translateSubjectType(log.subject_type)
    : null;

  return (
    <div className='group hover:bg-accent/50 flex items-start gap-3 rounded-lg px-3 py-3 transition-colors'>
      <div className='relative shrink-0'>
        <div className='bg-primary/10 text-primary flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold'>
          {log.user?.name?.charAt(0).toUpperCase() ?? '?'}
        </div>
        <span
          className={cn(
            'ring-background absolute -end-0.5 -bottom-0.5 h-2.5 w-2.5 rounded-full ring-2',
            style.dot
          )}
        />
      </div>

      <div className='min-w-0 flex-1'>
        <div className='flex items-center justify-between gap-2'>
          <p className='truncate text-sm font-medium'>
            {log.user?.name ?? systemLabel}
          </p>
          <span className='text-muted-foreground shrink-0 text-[11px] tabular-nums'>
            {relativeTime(log.created_at)}
          </span>
        </div>

        <div className='mt-1.5 flex flex-wrap items-center gap-1.5'>
          <Badge
            variant='secondary'
            className={cn('h-5 gap-1 px-1.5 text-[11px] font-medium', style.bg)}
          >
            <ActionIcon action={log.action} className='h-3 w-3' />
            {translateAction(log.action)}
          </Badge>
          {subjectLabel && (
            <span className='text-muted-foreground text-[11px]'>
              {log.subject_name
                ? `${log.subject_name} (${subjectLabel})`
                : `${subjectLabel}${log.subject_id ? ` #${log.subject_id}` : ''}`}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

const CLEARED_ID_KEY = 'system-logs-cleared-id';

function readClearedId(): number | null {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem(CLEARED_ID_KEY);
  if (stored === null) return null;
  const parsed = Number(stored);
  return Number.isFinite(parsed) ? parsed : null;
}

function writeClearedId(id: number | null) {
  if (id === null) {
    localStorage.removeItem(CLEARED_ID_KEY);
  } else {
    localStorage.setItem(CLEARED_ID_KEY, String(id));
  }
}

export function SystemLogsDrawer({
  fallbackToPolling = true
}: {
  fallbackToPolling?: boolean;
}) {
  // #logs — app-shell drawer open-state in the URL hash.
  const { open, onOpenChange: setHashOpen } = useHashDialog(
    SYSTEM_LOGS_HASH.drawer
  );
  const [hasNew, setHasNew] = useState(false);
  const [clearedId, setClearedId] = useState<number | null>(readClearedId());
  const locale = useLocale();
  const t = useTranslations('systemLogs');
  const relativeTime = useRelativeTime('systemLogs');
  const { hasPermission, isSuperAdmin } = usePermissions();
  const { translateAction, translateSubjectType } = useLogFormatters();
  const clearedIdRef = useRef<number | null>(clearedId);
  const notifiedIdRef = useRef<number | null>(null);

  const canView = isSuperAdmin || hasPermission('system_logs.view');

  const { data, isPending } = useQuery({
    ...systemLogQueries.latestPolling({ pollingEnabled: fallbackToPolling }),
    // Gated on permission ONLY. `fallbackToPolling` decides whether to POLL
    // (it feeds `refetchInterval` above), never whether to fetch at all —
    // disabling the query left it `pending` forever, so the drawer rendered
    // skeletons and `use-dashboard-live`'s invalidation on `.audit-log-recorded`
    // had a disabled query to refetch, which is a no-op. Socket up: one fetch
    // plus a refetch per broadcast. Socket down: the 30s poll takes over.
    enabled: canView,
    select: (response) => {
      const logs = (response?.data ?? []) as AuditLog[];
      const latestId = logs[0]?.id ?? null;

      if (
        latestId !== null &&
        clearedIdRef.current !== null &&
        latestId > clearedIdRef.current &&
        latestId !== notifiedIdRef.current
      ) {
        notifiedIdRef.current = latestId;
        setHasNew(true);
      }

      return logs;
    }
  });

  const logs = (data ?? []).filter(
    (log) => clearedId === null || log.id > clearedId
  );

  const handleClear = useCallback(() => {
    const latestId = (data ?? [])[0]?.id ?? null;
    clearedIdRef.current = latestId;
    notifiedIdRef.current = null;
    writeClearedId(latestId);
    setClearedId(latestId);
    setHasNew(false);
  }, [data]);

  const handleOpenChange = useCallback(
    (value: boolean) => {
      setHashOpen(value);
      if (value) {
        setHasNew(false);
      }
    },
    [setHashOpen]
  );

  if (!canView) return null;

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <SheetTrigger asChild>
            <Button variant='ghost' size='icon' className='relative'>
              <ScrollText className='h-4 w-4' />
              {hasNew && (
                <span className='bg-destructive ring-background absolute end-1.5 top-1.5 h-2 w-2 rounded-full ring-2' />
              )}
              <span className='sr-only'>{t('latestActivity')}</span>
            </Button>
          </SheetTrigger>
        </TooltipTrigger>
        <TooltipContent>{t('latestActivity')}</TooltipContent>
      </Tooltip>

      <SheetContent
        side={locale === 'ar' ? 'left' : 'right'}
        className='flex w-full flex-col sm:w-[440px]'
      >
        <SheetHeader className='flex flex-col items-start gap-2'>
          <div className='flex items-center gap-2'>
            <Activity className='text-primary h-4 w-4' />
            <SheetTitle className='text-base'>{t('latestActivity')}</SheetTitle>
          </div>
          {logs.length > 0 && (
            <Button
              variant='ghost'
              size='sm'
              onClick={handleClear}
              className='text-muted-foreground hover:text-foreground h-7 gap-1 px-2 text-xs'
            >
              <X className='h-3 w-3' />
              {t('clearAll')}
            </Button>
          )}
        </SheetHeader>

        <Separator />

        {hasNew && !open && (
          <div className='bg-primary/10 text-primary flex items-center gap-2 rounded-md px-3 py-2 text-xs font-medium'>
            <span className='bg-primary h-1.5 w-1.5 animate-pulse rounded-full' />
            {t('newLogs')}
          </div>
        )}

        <ScrollArea className='-mx-2 flex-1 overflow-hidden'>
          <div className='px-2'>
            {isPending ? (
              <div className='divide-border divide-y'>
                {Array.from({ length: 5 }).map((_, i) => (
                  <LogItemSkeleton key={i} />
                ))}
              </div>
            ) : logs.length === 0 ? (
              <div className='flex flex-col items-center justify-center py-16 text-center'>
                <div className='bg-muted mb-4 flex h-12 w-12 items-center justify-center rounded-full'>
                  <ScrollText className='text-muted-foreground h-6 w-6' />
                </div>
                <p className='text-foreground text-sm font-medium'>
                  {t('noLogs')}
                </p>
                <p className='text-muted-foreground mt-1 max-w-[240px] text-xs'>
                  {t('noLogsHint')}
                </p>
              </div>
            ) : (
              <div className='divide-border divide-y'>
                {logs.map((log) => (
                  <LogItem
                    key={log.id}
                    log={log}
                    relativeTime={relativeTime}
                    systemLabel={t('systemAction')}
                    translateAction={translateAction}
                    translateSubjectType={translateSubjectType}
                  />
                ))}
              </div>
            )}
          </div>
        </ScrollArea>

        {logs.length > 0 && (
          <>
            <Separator />
            <div className='pt-1'>
              <Link
                href='/dashboard/system-logs'
                onClick={() => setHashOpen(false)}
                className='text-muted-foreground hover:text-primary flex items-center justify-center gap-1.5 py-2 text-sm font-medium transition-colors'
              >
                {t('viewAll')}
                <ExternalLink className='h-3.5 w-3.5' />
              </Link>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
